/**
 * StepComplete — Final onboarding step: success screen.
 *
 * Shows checkmarks for completed steps, "Go to Dashboard" button.
 * Calls POST /api/onboarding/wizard/complete to mark onboarding finished.
 */
import { useState, useEffect } from "react";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { api } from "@/lib/api";

interface StepCompleteProps {
  orgId: string;
  onFinish(): void;
}

interface CompletionItem {
  label: string;
  done: boolean;
}

export function StepComplete({ orgId, onFinish }: StepCompleteProps) {
  const [isCompleting, setIsCompleting] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const items: CompletionItem[] = [
    { label: "Organization created", done: true },
    { label: "Channel connected", done: true },
    { label: "Escalation configured", done: true },
    { label: "Ready to go!", done: true },
  ];

  useEffect(() => {
    let cancelled = false;

    async function complete() {
      try {
        await api.post("/api/onboarding/wizard/complete", { orgId });
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to complete onboarding",
          );
        }
      } finally {
        if (!cancelled) setIsCompleting(false);
      }
    }

    complete();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
        <CheckCircle className="h-8 w-8 text-success" />
      </div>

      <div>
        <h2 className="text-xl font-semibold text-white">You're All Set!</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your workspace is ready. Here's what we set up:
        </p>
      </div>

      <ul className="flex w-full flex-col gap-3 text-left">
        {items.map((item) => (
          <li
            key={item.label}
            className="flex items-center gap-3 rounded-lg border border-white/10 bg-surface px-4 py-3"
          >
            <CheckCircle className="h-5 w-5 shrink-0 text-success" />
            <span className="text-sm text-foreground">{item.label}</span>
          </li>
        ))}
      </ul>

      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      {isCompleting ? (
        <Spinner size="md" />
      ) : (
        <Button onClick={onFinish} className="w-full" size="lg">
          Go to Dashboard
        </Button>
      )}
    </div>
  );
}
